import { useCallback } from "react";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useDraftMode } from "@/contexts/DraftModeContext";
import { useHandleNurseChange } from "@/hooks/useHandleNurseChange";
import { useScheduleDayOf } from "@/hooks/useScheduleDayOf";
import { useScheduleSaves } from "@/hooks/useScheduleSaves";
import type { NurseInShift } from "@/utils/models";
import { getShiftTypeByShiftTimes } from "@/utils/utilFunctions";

/** The shift type an edited shift is saved under: new hours may make it another type; a location or job edit keeps its own. */
export const editedShiftType = (originalNurseInShift: NurseInShift, updatedNurseInShift: NurseInShift, shiftTypes: readonly string[]): string =>
	hasChangedTimes(originalNurseInShift, updatedNurseInShift)
		? getShiftTypeByShiftTimes(shiftTypes, { startTime: updatedNurseInShift.startTime, endTime: updatedNurseInShift.endTime })
		: originalNurseInShift.shiftType;

const hasChangedTimes = (originalNurseInShift: NurseInShift, updatedNurseInShift: NurseInShift): boolean =>
	originalNurseInShift.startTime !== updatedNurseInShift.startTime || originalNurseInShift.endTime !== updatedNurseInShift.endTime;

export const useUpdateLocationAndHoursForShift = () => {
	const { chosenDepartment } = useChosenDepartment();
	const scheduleDayOf = useScheduleDayOf();
	const { updateNurseLocationAndTimes } = useScheduleSaves();
	const { updateNurse, isLoading: isLoadingTypes } = useHandleNurseChange();
	const { updateNurse: updateNurseLocationOrTimesLoading } = isLoadingTypes;
	const { isDraftMode } = useDraftMode();

	const updateLocationAndHours = useCallback(
		async ({ updatedNurseInShift, originalNurseInShift }: { updatedNurseInShift: NurseInShift; originalNurseInShift: NurseInShift }): Promise<boolean> => {
			// scheduleDayOf already applies getShiftDisplayDate. Do NOT wrap the start time with it here: applying it twice
			// moves the date twice for a PM day start when the start time falls exactly at the day-start hour.
			const { date } = scheduleDayOf(updatedNurseInShift);
			const didChangedTimes = hasChangedTimes(originalNurseInShift, updatedNurseInShift);
			const updatedNurseInShiftWithShiftType = {
				...updatedNurseInShift,
				shiftType: editedShiftType(originalNurseInShift, updatedNurseInShift, chosenDepartment.shift_types),
			};
			const didChangeLocation = originalNurseInShift.location !== updatedNurseInShiftWithShiftType.location;
			const didChangeJob = originalNurseInShift.jobId !== updatedNurseInShiftWithShiftType.jobId;
			if (!didChangedTimes && !didChangeLocation && !didChangeJob) {
				return false;
			}
			let success: boolean;
			try {
				await updateNurse({
					nurse: updatedNurseInShiftWithShiftType,
					date,
					shiftType: updatedNurseInShiftWithShiftType.shiftType,
				});
				success = true;
			} catch {
				//error handled inside mutate
				success = false;
			}
			if (!success) {
				return false;
			}
			updateNurseLocationAndTimes({
				nurseInShiftId: originalNurseInShift.id,
				date,
				// The shift is listed under its own type, which the start hour and the department's order don't always give.
				shiftType: originalNurseInShift.shiftType,
				actual: false,
				newStartTime: didChangedTimes ? updatedNurseInShiftWithShiftType.startTime : undefined,
				newEndTime: didChangedTimes ? updatedNurseInShiftWithShiftType.endTime : undefined,
				newLocation: didChangeLocation ? updatedNurseInShiftWithShiftType.location : undefined,
				newJobId: didChangeJob ? updatedNurseInShiftWithShiftType.jobId : undefined,
				newJobName: didChangeJob ? updatedNurseInShiftWithShiftType.jobName : undefined,
				isDraftMode,
			});
			return true;
		},
		[updateNurse, scheduleDayOf, chosenDepartment, isDraftMode, updateNurseLocationAndTimes],
	);

	return { updateLocationAndHours, updateNurseLocationOrTimesLoading };
};
